<template>
  <div v-if="contact">
    <v-card class="mb-4">

      <div class="px-4 pt-4 pb-0">
        <v-text-field v-model="templateName"
                      label="Template"
                      outlined
                      dense
                      disabled
                      hide-details
                      class="mb-4">
        </v-text-field>

        <v-text-field v-model="contact.name_email"
                      label="Recipient"
                      outlined
                      dense
                      disabled
                      hide-details
                      class="mb-4">
        </v-text-field>

        <v-text-field v-if="showCCRecipients"
                      :value="ccRecipients"
                      label="CC"
                      outlined
                      dense
                      disabled
                      hide-details
                      class="mb-4">
        </v-text-field>

        <v-text-field v-if="showBCCRecipients"
                      :value="bccRecipients"
                      label="BCC"
                      outlined
                      dense
                      disabled
                      hide-details
                      class="mb-4">
        </v-text-field>

        <v-select v-if="subjectLines.length > 1"
                  v-model="form.subject"
                  :items="subjectLines"
                  item-text="subject"
                  item-value="subject"
                  label="Subject"
                  outlined
                  dense
                  required>
        </v-select>
        <v-text-field v-else
                      v-model="form.subject"
                      label="Subject"
                      outlined
                      dense
                      placeholder="Write your subject line here. Try to keep it under 41 characters or 7 words."
                      :disabled="isTemplateSubjectLocked"
                      :hint="isTemplateSubjectLocked ? 'You cannot edit the subject for this template.' : 'Try to keep it under 41 characters or 7 words.'"
                      :persistent-hint="isTemplateSubjectLocked"
                      :error-messages="form.errors.get('subject')"
                      :rules="[rules.required]"
                      hide-details
                      class="mb-4">
        </v-text-field>

        <v-alert v-if="template && false === isContentEditable"
                 type="info"
                 dense
                 tile
                 class="mb-0">
          You are not allowed to edit this email template.
        </v-alert>

        <div v-if="useTinyMCE"
             id="tinyContainer"
             class="mt-3"
             :class="{'has-error': form.errors.has('html_body')}"
             :style="`min-height: ${tinyMinHeight}px`">
          <tinymce-editor v-if="mounted"
                          api-key="w7qx9l4xwuc114tjfvqngdobxbi48cjwt782qof3qt0ht031"
                          id="tinyEditor"
                          :init="tinyConfig"
                          v-model="form.html_body">
          </tinymce-editor>
          <tinymce-editor v-if="mounted"
                          api-key="w7qx9l4xwuc114tjfvqngdobxbi48cjwt782qof3qt0ht031"
                          id="hiddenTinyEditor"
                          style="display: none !important; height: 0 !important; outline: none;"
                          :init="hiddenTinyConfig">
          </tinymce-editor>
        </div>
        <template v-else>
          <div v-if="dataURI"
               style="width: 100%; height: 600px;">
            <iframe :src="dataURI"
                    style="border: 1px solid #aaaaaa; overflow-y: auto;">
            </iframe>
          </div>
        </template>
      </div>
      <v-card-actions>
        <v-btn @click.prevent="goBack">
          Previous
        </v-btn>
        <v-spacer></v-spacer>
        <v-checkbox v-model="form.send_receipt"
                    hide-details
                    label="Send me a confirmation"
                    class="ma-0 mr-2">
        </v-checkbox>
        <v-btn v-if="useTinyMCE"
               @click.prevent="preview">
          Preview
        </v-btn>
        <v-btn v-if="canScheduleEmails === false"
               @click.prevent="send"
               :disabled="form.busy"
               color="primary">
          Send
        </v-btn>
        <template v-else>
          <v-btn @click.prevent="send"
                 :disabled="form.busy || isPlaceholderPresent"
                 color="primary"
                 class="mr-2">
            Send Now
          </v-btn>
          <v-dialog v-model="scheduleDialog"
                    persistent
                    width="660px">
            <template v-slot:activator="{on, attrs}">
              <v-btn color="primary"
                     :disabled="form.busy || isPlaceholderPresent"
                     v-bind="attrs"
                     v-on="on"
                     @click="openScheduleSendDialog">
                <v-icon left>
                  fal fa-fw fa-clock
                </v-icon>
                Schedule Send
              </v-btn>
            </template>
            <template v-slot:default>
              <v-card flat
                      tile>
                <v-card-title>
                  Schedule send
                </v-card-title>
                <v-card-subtitle>
                  {{ scheduledTimeZone }}
                </v-card-subtitle>
                <v-card-text class="pb-0">
                  <v-row>
                    <v-col cols="6">
                      <v-select v-model="form.scheduled_date"
                                label="Date"
                                outlined
                                :items="scheduleDates"
                                item-value="id"
                                item-text="text"
                                required
                                @change="updateScheduledTime"
                                hide-details>
                      </v-select>
                    </v-col>
                    <v-col cols="6">
                      <v-select v-model="form.scheduled_time"
                                label="Time"
                                outlined
                                :disabled="!form.scheduled_date"
                                :items="scheduleTimes"
                                item-value="id"
                                item-text="text"
                                required
                                hide-details>
                      </v-select>
                    </v-col>
                  </v-row>

                  <p class="text-caption my-2">
                    You can schedule an email up to seven days in the future.
                  </p>

                  <v-alert type="info"
                           tile
                           class="mb-2">
                    <div class="font-weight-bold text-uppercase">
                      Once an email is scheduled, it cannot be canceled.
                    </div>
                    <div>
                      Ensure all details are correct before proceeding.
                    </div>
                  </v-alert>

                  <v-alert v-if="scheduleError"
                           type="error"
                           tile
                           class="mb-2">
                    {{ scheduleError.message ?? scheduleError }}
                  </v-alert>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn @click.prevent="cancelSchedule"
                         text>
                    Cancel
                  </v-btn>
                  <v-btn @click.prevent="scheduleSend"
                         :disabled="form.busy || isScheduleFormValid === false"
                         color="primary">
                    Schedule send
                  </v-btn>
                </v-card-actions>
              </v-card>
            </template>
          </v-dialog>
        </template>
      </v-card-actions>
    </v-card>
    <v-dialog v-if="previewDataURI"
              v-model="previewDialog"
              scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          Preview
          <v-spacer></v-spacer>
          <v-btn @click.prevent="previewDialog = false"
                 text
                 dark>
            Close
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-divider></v-divider>
          <div>
            From: {{ fromName }}
          </div>
          <div>
            Reply To: {{ user.reply_to_address ? user.reply_to_address : user.email }}
          </div>
          <div>
            <template v-if="contact.name && contact.email">
              To: {{ contact.name }} <{{ contact.email }}>
            </template>
            <template v-else>
              To: {{ contact.email }}
            </template>
          </div>
          <div v-if="previewData && previewData.subject">
            Subject: {{ previewData.subject }}
          </div>
          <v-divider></v-divider>
        </v-card-text>
        <v-responsive class="mx-4 embed-responsive"
                      :aspect-ratio="4/3">
          <iframe :src="previewDataURI">
          </iframe>
        </v-responsive>
      </v-card>
    </v-dialog>
    <ContentPickerDialog v-if="showContentPicker"
                         :dialog="showContentPicker"
                         :form="form"
                         @close="closeContentPicker"
                         @insert="createPresentationLinks">
    </ContentPickerDialog>

    <SpacePickerDialog v-if="showSpacePicker"
                       :dialog="showSpacePicker"
                       :form="form"
                       @close="closeSpacePicker"
                       @insert="createSpaceLinks">
    </SpacePickerDialog>

    <v-dialog v-model="showCannotShareDialog"
              max-width="500">
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          Content Sharing Disabled
        </v-card-title>
        <div class="pa-4">
          <p>
            You are not allowed to share content in this template.
          </p>
        </div>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text
                 color="primary"
                 @click.prevent="showCannotShareDialog = false">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <EmailTemplateIntructionsDialog v-if="showInstructionsDialog"
                                    :dialog="showInstructionsDialog"
                                    :template="template"
                                    max-width="500"
                                    persistent
                                    @close="closeInstructions">
    </EmailTemplateIntructionsDialog>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import shareTemplateService from '../../../../../ml/services/shareTemplateService';
import tinymceService from '../../../../../ml/services/tinymceService';
import sharedLinkService from '../../../../../ml/services/sharedLinkService';
import ContentPickerDialog from './components/ContentPickerDialog';
import emailService from '../../../../../ml/services/emailService';
import presentationService from '../../../../../ml/services/presentationService';
import {isArray, isInteger, isNull, isObject, isString} from 'lodash';
import mobilelockerEditor from '../../share_templates/components/tinymceMobileLocker';
import TagsTable from '../../share_templates/components/TagsTable';
import goTo from 'vuetify/lib/services/goto'; // MLD-3922
import DOMPurify from 'dompurify';
import SpacePickerDialog from './components/SpacePickerDialog.vue'; // MLD-4565
import {formValidation} from '../../../plugins';
import htmlService from '../../../services/htmlService';
import moment from 'moment-timezone';
import timezoneService from '../../../services/timezoneService';
import EmailTemplateIntructionsDialog from '../components/EmailTemplateIntructionsDialog.vue';

const scheduleMinutesInterval = 5;

/**
 * MLD-6106
 * @returns {moment.Moment}
 */
function calculateStartTime() {
  return moment().minutes(Math.ceil(moment().minutes() / scheduleMinutesInterval) * scheduleMinutesInterval);
}

export default {
  name: 'email-wizard-compose',
  mixins: [formValidation],
  components: {EmailTemplateIntructionsDialog, TagsTable, ContentPickerDialog, SpacePickerDialog},
  data() {
    return {
      editor: null,
      hiddenEditor: null,
      editorEnabled: false,
      error: null,
      form: new Form({
        version: 1,
        template_id: '',
        presentations: [],
        spaces: [], // MLD-4908
        subject: '',
        html_body: '',
        include_images: false,
        is_test: false, // MLD-2066
        notification_level: 2,
        recipients: [],
        send_receipt: true, // MLD-2277
        is_scheduled: false,
        scheduled_at: null,
        scheduled_date: null,
        scheduled_time: null,
      }),
      mounted: false,
      links: [],
      spaceIDs: [], // MLD-4908
      notificationLevels: sharedLinkService.getNotificationLevels(),
      presentation: null,
      tinyError: false, // MLD-2759
      previewData: null,
      previewHTML: null, // MLD-2759
      previewDialog: false, // MLD-2759
      showContentPicker: false,
      showSpacePicker: false, // MLD-4908
      showCannotShareDialog: false,
      showInstructionsDialog: false, // MLD-3031
      scheduleDialog: false, // MLD-6106
      scheduleError: null, // MLD-6106
      subjectLines: [], // MLD-6517
    }
  },
  computed: {
    ...mapGetters([
      'canScheduleEmails', // MLD-6106
      'canUseSnippets', // MLD-3900
      'canUserShareSpaces', // MLD-5030
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'mustExitProperly',
      'presentations',
      'activeSnippets', // MLD-3900
    ]),
    ...mapGetters('emailCart', [
      'cart',
      'contact',
      'template',
    ]),
    showBCCRecipients() { // MLD-5567
      return this.team?.sharing_bcc_field_enabled
        && this.template?.bcc_recipients?.length > 0;
    },
    bccRecipients() { // MLD-5567
      if (this.showBCCRecipients) {
        return this.template.bcc_recipients.join(', ');
      }
      return null;
    },
    showCCRecipients() { // MLD-5566
      return this.team?.sharing_cc_field_enabled
        && this.template?.cc_recipients?.length > 0;
    },
    ccRecipients() { // MLD-5566
      if (this.showCCRecipients) {
        return this.template.cc_recipients.join(', ');
      }
      return null;
    },
    isElectron() { // MLD-3108
      return this.$route
        && this.$route.query
        && this.$route.query.source
        && this.$route.query.source === 'electron';
    },
    isPlaceholderPresent() { // MLD-3143
      return isObject(this.form)
        && isString(this.form.html_body)
        && this.form.html_body.includes('[INSERT LINKS HERE]');
    },
    isIOS() {
      return this.$route
        && this.$route.query
        && this.$route.query.source
        && this.$route.query.source === 'ios';
    },
    tags() { // MLD-2726
      const tags = [
        {tag: 'user_signature', name: 'My signature'},
      ];
      if (this.contact) {
        tags.push({tag: 'recipient.name', name: `Contact name: ${this.contact.name}`})
        if (this.contact.greeting) {
          tags.push({tag: 'recipient.greeting', name: `Contact greeting: ${this.contact.greeting}`});
        }
        if (this.contact.salutation) {
          tags.push({tag: 'recipient.salutation', name: `Contact salutation: ${this.contact.salutation}`});
        }
        tags.push({tag: 'recipient.first_name', name: `Contact first name: ${this.contact.first_name}`});
        tags.push({tag: 'recipient.last_name', name: `Contact last name: ${this.contact.last_name}`});
      }
      if (this.presentation) {
        tags.push({tag: 'presentation_name', name: `File name: ${DOMPurify.sanitize(this.presentation.name)}`});
      }
      return tags;
    },
    isContentEditable() { // MLD-3769
      if (!this.template) {
        return true;
      }
      return this.template
        && this.template.is_editable;
    },
    contextMenu() {
      let items = [
        'context_menu_ml_insert_content',
        'context_menu_ml_insert_spaces',
      ]
      if (this.canShareLinks === false) {
        items = items.filter(i => i !== 'context_menu_ml_insert_content');
      }
      if (this.canShareSpaces === false) {
        items = items.filter(i => i !== 'context_menu_ml_insert_spaces');
      }
      return items.join(' ');
    },
    quickbarsSelectionToolbar() { // MLD-4908
      let items = [
        'button_ml_insert_content',
        'button_ml_insert_spaces'
      ]
      if (this.canShareLinks === false) {
        items = items.filter(i => i !== 'button_ml_insert_content');
      }
      if (this.canShareSpaces === false) {
        items = items.filter(i => i !== 'button_ml_insert_spaces');
      }
      return items.join(' | ');
    },
    toolbar() { // MLD-4908
      let items = [
        'toggle_button_ml_fullscreen',
        'button_ml_insert_content',
        'button_ml_insert_spaces',
        'menu_button_ml_snippet',
        'menu_button_ml_fragments',
        'undo redo',
        'bold italic underline removeformat',
        'numlist bullist',
        'link',
      ];
      if (this.canShareLinks === false) {
        items = items.filter(i => i !== 'button_ml_insert_content');
      }
      if (this.canShareSpaces === false) {
        items = items.filter(i => i !== 'button_ml_insert_spaces');
      }
      return items.join(' | ');
    },
    canShareSpaces() { // MLD-4908
      if (this.template) {
        return this.canUserShareSpaces // MLD-5030
          && this.isContentEditable
          && this.template.can_share_links;
      }
      return this.canUserShareSpaces;
    },
    canShareLinks() {
      if (!this.template) {
        return true;
      }
      return this.isContentEditable // MLD-3769
        && this.template.can_share_links;
    },
    isSubjectEditable() {
      if (!this.template) {
        return true;
      }
      return this.template
        && this.template.is_subject_editable;
    },
    isTemplateSubjectLocked() { // MLD-2695
      return this.template
        && !this.template.is_subject_editable;
    },
    canInsertSpaceLinks() { // MLD-4908
      if (!this.useTinyMCE) {
        return false;
      }
      return isArray(this.form.spaces)
        && this.form.spaces.length > 0;
    },
    canInsertPresentationLinks() {
      if (!this.useTinyMCE) {
        return false;
      }
      return isArray(this.form.presentations)
        && this.form.presentations.length > 0;
    },
    dataURI() { // MLD-2473
      if (this.template?.is_unlayer) { // MLD-6455
        let html = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD XHTML 1.0 Transitional //EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd"><html xmlns="http://www.w3.org/1999/xhtml" lang="en"><head>${this.template?.html_head ?? ''}</head><body>${this.form.html_body}</body></html>`
        if (this.form.html_body?.startsWith('<body')) {
          html = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD XHTML 1.0 Transitional //EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd"><html xmlns="http://www.w3.org/1999/xhtml" lang="en"><head>${this.template?.html_head ?? ''}</head>${this.form.html_body}</html>`
        }
        return htmlService.dataURI(html);
      } else if (this.form.html_body) {
        return htmlService.dataURI(this.form.html_body);
      }
      return null;
    },
    fromName() {
      if (this.previewData && this.previewData.from_name && this.previewData.from_address) {
        return `${this.previewData.from_name} <${this.previewData.from_address}>`
      }
      return this.user.name;
    },
    previewDataURI() {
      if (this.previewHTML) {
        const head = this.template && this.template.html_head ? this.template.html_head : '';
        const html = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD XHTML 1.0 Transitional //EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd"><html xmlns="http://www.w3.org/1999/xhtml" lang="en"><head>${head}</head><body>${this.previewHTML}</body></html>`;
        return htmlService.dataURI(html);
      }
      return null;
    },
    presentationID() {
      const presentationID = parseInt(this.$route.query.presentationID);
      if (isInteger(presentationID)) {
        return presentationID;
      }
      return null;
    },
    templateName() {
      if (this.template) {
        return this.template.name;
      }
      return 'None';
    },
    useTinyMCE() { // MLD-2473
      if (this.template) { // TinyMCE and Unlayer templates can use TinyMCE
        if (false === this.isContentEditable) { // MLD-3769 unless the template is not editable.
          return false;
        }
        return this.template.is_tinymce || this.template.is_unlayer;
      }
      return true;
    },
    tinyMinHeight() {
      const defaultHeight = 100;
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return 220
        case 'sm':
          return defaultHeight
        case 'md':
          return defaultHeight
        case 'lg':
          return defaultHeight + 100;
        case 'xl':
          return defaultHeight + 100;
        default:
          return defaultHeight;
      }
    },
    hiddenTinyConfig() {
      const config = {
        //content_css: `/v2/css/tinymce_editor.css?v=${new Date().getTime()}`, // MLD-2694
        selector: '#hiddenTinyEditor',
        resize: false,
        min_height: 0,//this.tinyMinHeight,
        height: 0,//this.tinyHeight,
        menubar: false,
        quickbars_insert_toolbar: false,
        quickbars_selection_toolbar: false, // MLD-2693 @see https://www.tiny.cloud/docs/plugins/opensource/quickbars/#quickbars_selection_toolbar
        plugins: 'mobilelockereditor anchor charmap code codesample emoticons fullscreen help hr image link lists media nonbreaking noneditable paste preview quickbars searchreplace tabfocus template textpattern visualblocks',
        toolbar: false,
        statusbar: false, // @see https://www.tiny.cloud/docs/general-configuration-guide/customize-ui/#hidingthestatusbar
        style_formats_autohide: true,
        table_toolbar: '', // @see https://www.tiny.cloud/docs/plugins/opensource/table/#table_toolbar
        visual: false, // @see https://www.tiny.cloud/docs/configure/content-appearance/#visual
        contextmenu: '',
        setup: (editor) => {
          this.hiddenEditor = editor;
        },
      }
      return tinymceService.getConfig(config);
    },
    tinyConfig() {
      let tableToolbar = '';

      const config = {
        content_css: `/v2/css/tinymce_editor.css?v=${new Date().getTime()}`, // MLD-2694
        selector: '#tinyEditor',
        resize: true,
        min_height: this.tinyMinHeight,
        //height: this.tinyHeight,
        menubar: '',
        //quickbars_insert_toolbar: 'ml_quick_insert', // MLD-2693 @see https://www.tiny.cloud/docs/plugins/opensource/quickbars/#exampledisablingthequickinsertcontexttoolbar
        quickbars_insert_toolbar: false,
        //quickbars_selection_toolbar: 'button_ml_insert_content | bold italic | quicklink', // MLD-2693 @see https://www.tiny.cloud/docs/plugins/opensource/quickbars/#quickbars_selection_toolbar
        quickbars_selection_toolbar: this.quickbarsSelectionToolbar,
        plugins: 'mobilelockereditor anchor charmap code codesample emoticons fullscreen help hr image link lists media nonbreaking noneditable paste preview quickbars searchreplace table tabfocus template textpattern visualblocks',
        toolbar: this.toolbar,
        style_formats_autohide: true,
        table_toolbar: tableToolbar, // @see https://www.tiny.cloud/docs/plugins/opensource/table/#table_toolbar
        visual: false, // @see https://www.tiny.cloud/docs/configure/content-appearance/#visual
        contextmenu: this.contextMenu,
        init_instance_callback: (editor) => {
          const elm = editor.dom.select('#tinymce')
          editor.dom.addClass(elm, 'EditMode');
        },
        setup: (editor) => {
          this.editor = editor;

          // @see https://www.tiny.cloud/docs/advanced/events/#handlingeditorevents
          if (this.canShareLinks) {
            editor.on('SetContent', e => { // MLD-3143
              this.selectPlaceholder();
            })
          }

          editor.ui.registry.addToggleButton('toggle_button_ml_fullscreen', {
            text: 'Full screen',
            onAction: (api) => {
              editor.execCommand('mceFullScreen');
              api.setActive(!api.isActive());
            },
          });

          if (this.canShareLinks) {
            editor.ui.registry.addMenuItem('menuitem_ml_insert_content', {
              icon: 'link',
              text: this.presentationID ? 'Insert link here' : 'Insert links here',
              tooltip: this.presentationID ? 'Insert link at your cursor position' : 'Insert links at your cursor position',
              onSetup: (api) => {
                if (this.canShareLinks) {
                  editor.on('NodeChange', ({element}) => {
                    api.setDisabled(!mobilelockerEditor.isEditable(editor, element));
                  });
                }
                api.setDisabled(!this.canShareLinks);
              },
              onAction: (api) => {
                if (this.canShareLinks) {
                  this.openContentPicker();
                } else {
                  this.showCannotShareDialog = true;
                }
              },
            });
          }

          if (this.canShareSpaces) {
            editor.ui.registry.addMenuItem('menuitem_ml_insert_spaces', {
              icon: 'link',
              text: 'Insert spaces here',
              tooltip: 'Insert links to spaces at your cursor position',
              onSetup: (api) => {
                if (this.canShareSpaces) {
                  editor.on('NodeChange', ({element}) => {
                    api.setDisabled(!mobilelockerEditor.isEditable(editor, element));
                  });
                }
                api.setDisabled(!this.canShareSpaces);
              },
              onAction: (api) => {
                if (this.canShareSpaces) {
                  this.openSpacePicker();
                } else {
                  this.showCannotShareDialog = true;
                }
              },
            });
          }

          // @see https://www.tiny.cloud/docs/ui-components/contextmenu/
          if (this.canShareLinks) { // MLD-3769 don't show the context menu if the user isn't allowed to share links in this template.
            editor.ui.registry.addContextMenu('context_menu_ml_insert_content', {
              update: (element) => {
                return 'menuitem_ml_insert_content';
              }
            })

            editor.ui.registry.addButton('button_ml_insert_content', { // MLD-2693 @see https://www.tiny.cloud/docs/ui-components/toolbarbuttons/
              //disabled: !this.canShareLinks,
              text: 'Insert links',
              tooltip: 'Insert links to presentations at your cursor position',
              onSetup: (api) => {
                if (this.canShareLinks) {
                  editor.on('NodeChange', ({element}) => {
                    api.setDisabled(!mobilelockerEditor.isEditable(editor, element));
                  });
                }
                api.setDisabled(!this.canShareLinks);
              },
              onAction: () => {
                if (this.canShareLinks) {
                  this.openContentPicker();
                } else {
                  this.showCannotShareDialog = true;
                }
              },
            });
          }
          // @see https://www.tiny.cloud/docs/ui-components/contextmenu/
          if (this.canShareSpaces) {
            editor.ui.registry.addContextMenu('context_menu_ml_insert_spaces', {
              update: (element) => {
                return 'menuitem_ml_insert_spaces';
              }
            })

            // MLD-4908
            editor.ui.registry.addButton('button_ml_insert_spaces', {
              //disabled: !this.canShareLinks,
              text: 'Insert spaces',
              tooltip: 'Insert links to spaces at your cursor position',
              onSetup: (api) => {
                if (this.canShareSpaces) {
                  editor.on('NodeChange', ({element}) => {
                    api.setDisabled(!mobilelockerEditor.isEditable(editor, element));
                  });
                }
                api.setDisabled(!this.canShareSpaces);
              },
              onAction: () => {
                if (this.canShareSpaces) {
                  this.openSpacePicker();
                } else {
                  this.showCannotShareDialog = true;
                }
              },
            });
          }

          if (this.isContentEditable && this.canUseSnippets) { // MLD-3900
            editor.ui.registry.addMenuButton('menu_button_ml_snippet', {
              text: 'Insert snippet',
              fetch: (callback) => {
                let items = this.activeSnippets.map(snippet => {
                  return {
                    type: 'menuitem',
                    text: snippet.name,
                    onAction: () => {
                      const data = {
                        presentation_id: this.presentationID,
                        template_id: this.template ? this.template.id : null,
                        user_contact_id: this.contact ? this.contact.id : null,
                      }
                      shareTemplateService.renderText(snippet.content, data)
                        .then(data => {
                          editor.insertContent(data.output);
                        })
                        .catch(err => {
                          this.$toast.error(`There was a problem inserting that content`);
                        })
                    }
                  }
                });
                callback(items);
              }
            });
          }

          if (this.isContentEditable) {
            editor.ui.registry.addMenuButton('menu_button_ml_fragments', { // MLD-2726
              text: 'Insert fragments',
              fetch: (callback) => {
                let items = this.tags.map(tag => {
                  return {
                    type: 'menuitem',
                    text: tag.name,
                    onAction: () => {
                      const data = {
                        presentation_id: this.presentationID,
                        template_id: this.template ? this.template.id : null,
                        user_contact_id: this.contact ? this.contact.id : null,
                      }
                      shareTemplateService.renderText(`{{${tag.tag}}}`, data)
                        .then(data => {
                          editor.insertContent(data.output);
                        })
                        .catch(err => {
                          this.$toast.error(`There was a problem inserting that content`);
                        })
                    }
                  }
                });
                callback(items);
              }
            })
          }
        },
      }
      return tinymceService.getConfig(config);
    },
    returnPrompt() { // MLD-2947
      return `You cannot exit that way.`;
    },
    scheduleDates() { // MLD-6106
      const dates = [];
      let startDate = moment().startOf('day');
      const endDate = moment().startOf('day').add(7, 'days');

      while (startDate <= endDate) {
        dates.push({
          id: startDate.format('YYYY-MM-DD'),
          text: startDate.format('dddd, MMMM D, YYYY')
        });
        startDate = startDate.add(1, 'day');
      }

      return dates;
    },
    scheduleTimes() { // MLD-6106 @see https://chatgpt.com/c/2f17c818-af3a-4bc9-9284-2c88f91bd9d7
      const times = [];
      if (this.form.scheduled_date) {
        const selectedDate = moment(this.form.scheduled_date, 'YYYY-MM-DD');
        const currentDate = moment().startOf('day');
        const lastDate = moment().add(7, 'day');
        let startTime = moment('06:00', 'HH:mm');
        let endTime = moment('23:59', 'HH:mm');

        if (selectedDate.isSame(currentDate, 'day')) {
          startTime = moment();
          startTime = startTime.minutes(Math.ceil(startTime.minutes() / scheduleMinutesInterval) * scheduleMinutesInterval);
        }
        if (selectedDate.isSame(lastDate, 'day')) {
          endTime = moment(lastDate.format('HH:mm'), 'HH:mm');
        }

        while (startTime <= endTime) {
          times.push({
            id: startTime.format('HH:mm'),
            text: startTime.format('h:mm A')
          });
          startTime = startTime.add(scheduleMinutesInterval, 'minutes');
        }
      }

      return times;
    },
    scheduledTimeZone() {
      return timezoneService.getFriendlyTimezone()
    },
    computeScheduledAt() { // MLD-6106
      if (this.form.scheduled_date && this.form.scheduled_time) {
        return moment(`${this.form.scheduled_date} ${this.form.scheduled_time}`, 'YYYY-MM-DD HH:mm').utc();
      }
      return null;
    },
    isScheduleFormValid() { // MLD-6106
      if (!this.computeScheduledAt) {
        return false;
      }
      return this.computeScheduledAt.isAfter(moment())
        && this.computeScheduledAt.isBefore(moment().add(7, 'days'))
    },
  },
  beforeRouteLeave(to, from, next) { // MLD-2947
    if (this.mustExitProperly) {
      const leave = confirm('Are you sure you want to leave without sending the email?');
      //alert(this.returnPrompt);
      return next(leave);
    }
    next();
  },
  async beforeDestroy() { // MLD-2947
    window.removeEventListener('beforeunload', this.promptBeforeUnloading, {capture: true});
  },
  async created() {
    if (!this.contact) {
      //await this.$router.replace({name: `${this.$root.routePrefix}.email.wizard.recipient`, query: this.$route.query});
      await this.goBack();
      return;
    }
    this.initForm();
    await this.fetchData();
    window.addEventListener('beforeunload', this.promptBeforeUnloading, {capture: true}); // MLD-2947
  },
  mounted() {
    this.mounted = true;
    goTo(0); // MLD-3922
  },
  methods: {
    initForm() {
      this.form.send_receipt = !!this.user.send_confirmation_preference; // MLD-2782
      if (this.template) {
        this.form.template_id = this.template.id;
      }
    },
    toggleFullScreen() {
      if (this.editor) {
        this.editor.execCommand('mceFullScreen');
      }
    },
    openSpacePicker() { // MLD-4908
      console.log('openSpacePicker()');
      this.form.spaces = [];
      this.showSpacePicker = true;
    },
    closeSpacePicker() { // MLD-4908
      console.log('closeSpacePicker()');
      this.showSpacePicker = false;
    },
    openContentPicker() {
      console.log('openContentPicker()');
      this.form.presentations = [];
      this.showContentPicker = true;
    },
    closeContentPicker() {
      console.log('closeContentPicker()');
      this.showContentPicker = false;
    },
    async fetchData() {
      await this.$store.dispatch('content/fetchSnippets', {user_id: this.user.id});
      await this.getPresentation();
      this.$store.dispatch('content/fetchSpaces').then().catch(() => {
      });
      await this.renderTemplate();

      // MLD-6517
      if (this.template) {
        this.subjectLines = await shareTemplateService.getSubjectLinesFor(this.template.id);
      }

      // MLD-3108 - should be FALSE for electron and iOS app, TRUE everywhere else.
      let mustExitProperly = true;
      if (this.isElectron || this.isIOS) {
        mustExitProperly = false;
      }
      await this.$store.dispatch('content/setMustExitProperly', mustExitProperly); // MLD-2947
      if (this.template && this.template.instructions) {
        this.showInstructionsDialog = true;
      }
    },
    getPresentation() {
      if (this.presentationID) {
        return presentationService.getPresentation(this.presentationID)
          .then(data => {
            this.presentation = data;
            this.form.presentations = [this.presentationID];
          })
          .catch(err => {
            this.$toast.error(`There was a problem retrieving presentation ${DOMPurify.sanitize(this.presentationID)}.`);
            this.presentation = null;
            this.form.presentations = [];
          })
      } else {
        this.presentation = null;
        return Promise.resolve();
      }
    },
    renderTemplate() {
      if (this.template) {
        const form = {
          presentation_id: this.presentationID,
          user_contact_id: this.contact.id,
        }
        this.previewData = {
          subject: '',
          content: '',
          from_address: '',
          from_name: '',
          shared_link_url_parameters: null, // MLD-2954
        }
        shareTemplateService.render(this.template.id, form)
          .then(data => {
            this.previewData.from_address = data.from_address;
            this.previewData.from_name = data.from_name;
            this.previewData.subject = data.subject;
            this.previewData.content = this.decorateContent(data.content);
            this.previewData.shared_link_url_parameters = data.shared_link_url_parameters; // MLD-2954
            this.form.html_body = this.decorateContent(data.content);
            this.form.subject = data.subject;
            if (isArray(data.links)) { // MLD-5297
              this.links = data.links;
            }
          })
      }
    },
    decorateContent(content) { // MLD-3143
      if (content && this.canShareLinks) { // MLD-3769
        return content.replaceAll('[INSERT LINKS HERE]', '<span class="ML_insert_links">[INSERT LINKS HERE]</span>');
      }
      return content;
    },
    selectPlaceholder() { // MLD-3143
      // @see https://stackoverflow.com/a/51514655
      if (this.useTinyMCE && this.canShareLinks) {
        let selection = this.editor.dom.select(`span.ML_insert_links`);
        if (selection && selection.length) {
          selection = selection[0];
          this.editor.selection.select(selection);
          selection.scrollIntoView({behavior: "instant", block: "center", inline: "nearest"});
          // @see https://www.tiny.cloud/docs/api/tinymce/tinymce.editor/#focus
          this.editor.focus();
        }
      }
    },
    getHTMLBody() {
      if (this.previewHTML) {
        return this.previewHTML;
      }
      return this.form.html_body;
    },
    createSpaceLinks() { // MLD-4908
      console.log('createSpaceLinks()', this.form.spaces);
      this.closeSpacePicker();
      if (!this.canInsertSpaceLinks) {
        return;
      }
      const form = {
        expires_at: null,
        is_test: this.form.is_test, // MLD-2066
        notification_level: this.form.notification_level,
        send_now: false,
        send_receipt: false,
        send_reminders: false,
        source: 'compose',
        spaces: this.form.spaces ?? [],
        template_id: this.template?.id,
        user_contact_id: this.contact?.id,
      }
      sharedLinkService.bulkShareSpaces(form)
        .then(links => {
          if (isArray(links)) {
            links.forEach(link => {
              this.links.push(link);
            })
            form.spaces.forEach(spaceID => {
              this.spaceIDs.push(spaceID);
            })
            this.insertLinks(links);
          }
          this.form.spaces = [];
        })
        .catch(err => {
          this.$toast.error(`There was a problem creating the links.`, {timeout: 3000});
        })
    },
    createPresentationLinks(includeImages) {
      this.closeContentPicker();
      this.form.include_images = includeImages; // MLD-3113
      if (!this.canInsertPresentationLinks) {
        return;
      }
      const form = {
        expires_at: null,
        is_test: this.form.is_test, // MLD-2066
        notification_level: this.form.notification_level,
        presentation_id: this.form.presentations,
        recipients: [this.contact],
        send_now: false,
        send_receipt: false,
        send_reminders: false,
        source: 'compose',
        shared_link_url_parameters: this.previewData ? this.previewData.shared_link_url_parameters : null, // MLD-2954
      };
      sharedLinkService.bulkSharePresentations(form)
        .then(links => {
          if (isArray(links)) {
            links.forEach(link => {
              this.links.push(link);
            })
            this.insertLinks(links);
          }
          this.form.presentations = [];
        })
        .catch(err => {
          this.$toast.error(`There was a problem creating the links.`, {timeout: 3000});
        })
    },
    insertLinks(links) {
      const items = [];
      links.forEach(link => {
        if (link.presentation) {
          let linkHTML = `${link.html.plain}<br/>`;
          if (this.form.include_images) {
            if (link.html.page_thumbnail) {
              linkHTML = link.html.page_thumbnail;
            } else if (link.html.thumbnail) {
              linkHTML = link.html.thumbnail;
            }
          }
          items.push(linkHTML);
        } else if (link.space) { // MLD-4908
          const linkHTML = `${link.html.plain}<br/>`;
          items.push(linkHTML);
        }
      });
      const content = `${items.join('')}`;
      this.insertContent(content);
    },
    insertContent(content, overwrite = false) {
      if (this.useTinyMCE) {
        // @see https://www.tiny.cloud/docs/api/tinymce/root_tinymce/#get
        if (this.editor) {
          if (overwrite) {
            this.editor.setContent(content)
            this.editor.setDirty(true);
          } else {
            // @see https://www.tiny.cloud/docs/api/tinymce/root_tinymce/#execcommand
            if (this.editor.execCommand('insertHTML', false, content)) {
              //console.log('insertHTML worked');
            } else {
              this.$toast.warning('insertHTML failed', {timeout: 3000});
            }
          }
          //this.saveDraft(true);
        } else {
          this.$toast.warning('Could not get the editor', {timeout: 3000});
        }
      } else {
        this.form.html_body = content;
        //this.saveDraft(true);
      }
    },
    preview() {
      this.renderEmailContent();
      if (this.previewHTML) {
        this.previewDialog = true;
      }
    },
    renderEmailContent() {
      this.previewHTML = null;
      if (this.useTinyMCE && this.editor && this.hiddenEditor) {
        let originalContent = this.editor.getContent();
        this.hiddenEditor.setContent(originalContent);
        // Replace SELECT with the values
        this.tinyError = false;
        let sourceSelects = [];
        if (this.editor.dom) { // MLD-4584
          sourceSelects = this.editor.dom.select('select.mlPicker');
          sourceSelects.forEach(select => {
            if (isNull(select.value) || (isString(select.value) && select.value.trim() === '')) {
              this.tinyError = true;
            }
          });
        }
        if (this.tinyError) {
          this.$toast.error('You have to pick a value from each dropdown.');
          //this.$store.commit('setErrorMessage', 'You have to select an option');
          return;
        }
        if (this.hiddenEditor.dom) {
          const selects = this.hiddenEditor.dom.select('select.mlPicker');
          selects.forEach((select, index) => {
            //const value = select.value;
            const value = sourceSelects[index].value;
            if (value) {
              const span = document.createElement('span');
              this.hiddenEditor.dom.addClass(span, 'mlPickerValue');
              this.hiddenEditor.dom.setAttrib(span, 'data-value', value);
              this.hiddenEditor.dom.setAttrib(span, 'style', 'margin-right: 4px;');
              this.hiddenEditor.dom.setAttrib(span, 'data-select', JSON.stringify(this.hiddenEditor.serializer.serialize(select)));
              this.hiddenEditor.dom.setHTML(span, value);
              select.after(span);
              this.hiddenEditor.dom.remove(select);
            } else {
              this.tinyError = true;
            }
          })
        }
        this.previewHTML = this.hiddenEditor.getContent();
        const mode = this.hiddenEditor.mode.get();
        console.log(`hiddenEditor mode = ${mode}`);
        if (mode === 'design') {
          this.hiddenEditor.mode.set('readonly');
          console.log('Changed mode to READONLY');
        }
      } else {
        this.previewHTML = this.form.html_body;
      }
    },
    async promptForSchedule() { // MLD-6106
      this.scheduleDialog = true;
    },
    async cancelSchedule() { // MLD-6106
      this.form.scheduled_at = null;
      this.form.scheduled_date = null;
      this.form.scheduled_time = null;
      this.scheduleDialog = false;
    },
    async openScheduleSendDialog() { // MLD-6106
      this.form.scheduled_at = null;
      this.form.scheduled_date = moment().format('YYYY-MM-DD');
      this.form.scheduled_time = calculateStartTime().format('HH:mm');
    },
    async updateScheduledTime() { // MLD-6106
      if (this.isScheduleFormValid === false) {
        this.form.scheduled_time = '';
      }
    },
    async scheduleSend() { // MLD-6106
      this.scheduleError = null;
      if (this.isScheduleFormValid === false) {
        this.scheduleError = 'Invalid schedule time.'
        return;
      }
      await this.send();
      this.scheduleDialog = false;
    },
    async send() {
      if (this.form.busy) {
        return;
      }

      // MLD-2759
      this.renderEmailContent();
      if (this.tinyError) {
        return;
      }
      if (this.isPlaceholderPresent) { // MLD-3143
        this.$toast.warning('You have to remove [INSERT LINKS HERE] and click the "Insert links" button.');
        return;
      }

      this.error = null;
      this.form.startProcessing();
      const formData = {
        is_test: this.form.is_test, // MLD-2066
        links: this.links.map(l => l.id),
        spaces: this.spaceIDs, // MLD-4908
        preheader: this.template ? this.template.preheader_text : null, // MLD-2892
        presentations: this.links.map(l => l.presentation_id),
        query: this.$route.query, // MLD-2498
        send_receipt: this.form.send_receipt, // MLD-2277
        share_template_id: this.form.template_id ? this.form.template_id : null,
        user_contact_id: this.contact.id,
        scheduled_at: this.computeScheduledAt, // MLD-6106
      }
      const recipients = [this.contact];
      const subject = this.form.subject;
      const body = this.getHTMLBody();
      const css = this.template ? this.template.css : null; // MLD-2910
      try {
        await emailService.sendEmail(recipients, subject, body, css, formData)
        await this.updateSendConfirmationPreference(); // MLD-2782
        this.form.finishProcessing();
        this.$toast.success(`Your email will be sent to ${this.contact.email} shortly.`);
        await this.$store.dispatch('content/setMustExitProperly', false); // MLD-2947
        await this.$router.push({name: `${this.$root.routePrefix}.email.wizard`});
      } catch (e) {
        if (e.response?.data?.errors) { // MLD-4965
          this.form.errors.set(e.response.data.errors);
        }
        this.$toast.error('There was a problem sending your email.');
      } finally {
        this.form.busy = false;
      }
    },
    async updateSendConfirmationPreference() { // MLD-2782
      try {
        const form = {
          field: 'send_confirmation_preference',
          value: this.form.send_receipt,
        }
        await axios.put(`/api/user/preference`, form);
        this.$store.dispatch('fetchUser').then()
      } catch (e) {

      }
    },
    async goBack() {
      await this.$store.dispatch('content/setMustExitProperly', false); // MLD-2947
      await this.$store.dispatch('emailCart/prevStep');
      await this.$router.replace({name: `${this.$root.routePrefix}.email.wizard.recipient`, query: this.$route.query});
    },
    async promptBeforeUnloading(event) { // MLD-2947
      if (this.mustExitProperly) {
        event.preventDefault();
        return event.returnValue = this.returnPrompt;
      }
    },
    closeInstructions() { // MLD-3031
      this.showInstructionsDialog = false;
    }
  }
}
</script>
